import { useState, useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useNotification } from 'context/notification';
import fetchExpresso from 'utility/fetchExpresso';
import ProductForm from 'components/forms/ProductForm';
import moment from 'moment-timezone';

/**
 * productType 1 - Admission
 * productType 2 - Add-on
 * productType 3 - Assigned Seating (some features disabled)
 */

export default function ProductCreator(props) {
    const { productId, productType, hostId, eventId, onCreate, onCancel } = props;

    // *** Feature Flags *** //
    const enablePOS = productType !== 3;
    const enableQuantity = productType !== 3;
    const enableMultiScan = productType !== 3;
    const enableSeatPricing = productType === 3;

    const [loading, setLoading] = useState(false);

    const [init, setInit] = useState({
        status: 'loading', // loading, error, success
        defaultValues: null,
    });

    const { notify } = useNotification();

    // Fetch event start/end dates
    // Fetch latest product types
    useEffect(() => {
        (async () => {
            try {
                // ***** Fetch Event and Colors ***** //

                const [eventRequest, colorRequest, productGroupsRequest] = await Promise.all([
                    fetchExpresso(`/apiv2/events/${eventId}`),
                    fetchExpresso(`/apiv2/product_color`),
                    fetchExpresso(`/apiv2/events/${eventId}/product_groups`),
                ]);

                if (eventRequest.status !== 200) throw new Error();
                if (colorRequest.status !== 200) throw new Error();
                if (productGroupsRequest.status !== 200) throw new Error();

                const event = await eventRequest.json();
                const { colors } = await colorRequest.json();
                const { groups: productGroups } = await productGroupsRequest.json();

                // ***** Get Seat Pricing ***** //

                let seatPricing = [];

                if (enableSeatPricing) {
                    const seatPricingRequest = await fetchExpresso(
                        `/apiv2/events/${eventId}/seat-categories`,
                    );
                    if (seatPricingRequest.status !== 200) throw new Error();
                    const seatPricingResult = await seatPricingRequest.json();

                    seatPricing = seatPricingResult.categories.map((category) => ({
                        priceId: null, // Used when editing existing products
                        key: category.key,
                        name: category.name,
                        enabled: false,
                        price: '',
                    }));
                }

                // ***** Copy Existing Product ***** //

                if (productId) {
                    const productResponse = await fetchExpresso(`/apiv2/products/${productId}`);
                    if (productResponse.status !== 200) throw new Error();
                    const product = await productResponse.json();

                    let audience;

                    if (product.access_code) {
                        audience = 'accessCode';
                    } else if (product.visible === 1) {
                        audience = 'visible';
                    } else {
                        audience = 'hidden';
                    }

                    let posVisible = 'hidden';
                    if (product.pos_visible === 1) posVisible = 'visible';

                    setInit({
                        status: 'success',
                        enableSponsor: event.sponsor_module_flag === 1,
                        attendeeRegistration: event.checkout_question_mode === 2,
                        deletable: product.bundle_count === 0 && product.quantity_sold === 0,
                        labelColors: colors,
                        productGroups: productGroups ?? [],
                        defaultValues: {
                            name: product.prod_name + ' (copy)',
                            price: product.price,
                            free: !product.price,
                            seatPricing: seatPricing,
                            type: product.prod_type_id,
                            description: product.prod_desc,
                            startDate: moment(product.scan_start_date),
                            endDate: moment(product.scan_end_date),
                            eventStartDate: moment(event.start_date),
                            eventEndDate: moment(event.end_date),
                            labelMessage: product.label,
                            labelColor: product.label_color_id,
                            saleStartDate: moment(product.sale_start_date),
                            saleEndDate: moment(product.sale_end_date),
                            quantity: product.qty_total_available,
                            minOrder: product.qty_min_purchase,
                            maxOrder: product.qty_max_purchase,
                            overrideDates: Boolean(product.scan_date_override),
                            multipleScan: Boolean(product.multi_day_entry),
                            unlimitedEntry: Boolean(product.unlimited_entry),
                            //image: product.sponsor_image_url, TODO: if the user want to copy the existing image, we need to download the raw image file an re upload it
                            audience: audience,
                            accessCode: product.access_code,
                            quantitySold: product.quantity_sold,
                            posVisible: posVisible,
                            posName: product.pos_prod_name,
                            posPrice: product.pos_price,
                            productGroupId: product.prod_group_id,
                            isAttendee: undefined,
                        },
                    });
                }

                // ***** Create New Product ***** //
                else {
                    setInit({
                        status: 'success',
                        enableSponsor: event.sponsor_module_flag === 1,
                        labelColors: colors,
                        productGroups: productGroups ?? [],
                        attendeeRegistration: event.checkout_question_mode === 2,
                        defaultValues: {
                            seatPricing: seatPricing,
                            startDate: moment(event.start_date),
                            endDate: moment(event.end_date),
                            eventStartDate: moment(event.start_date),
                            eventEndDate: moment(event.end_date),
                            saleStartDate: moment({ hour: 0, minute: 0 }),
                            saleEndDate: moment(event.end_date),
                            posVisible: enablePOS ? 'visible' : 'hidden',
                            productGroupId: null,
                        },
                    });
                }
            } catch (e) {
                setInit({
                    status: 'error',
                    defaultValues: null,
                });
            }
        })();
    }, [productId, eventId, enablePOS, enableSeatPricing]);

    const handleSubmit = (formData) => {
        if (loading) return;
        setLoading(true);

        // Format and set Actions for Seat Pricing
        if (enableSeatPricing) {
            formData.seatPricing = formData.seatPricing
                .filter((category) => category.enabled)
                .map((category) => ({
                    key: category.key,
                    price: category.enabled ? Number(category.price) : 0,
                }));
        }

        fetchExpresso(`/apiv2/products/`, {
            method: 'POST',
            body: {
                ...formData,
                eventId: eventId,
                type: productType,
            },
        })
            .then((res) => {
                if (res.status === 200) {
                    notify.success('Ticket Created');
                    onCreate({
                        name: formData.name,
                        isAttendee: Boolean(formData.isAttendee),
                    });
                } else {
                    notify.error('There was a problem creating your ticket');
                    setLoading(false);
                }
            })
            .catch((e) => {
                notify.error('There was a problem creating your ticket');
                setLoading(false);
            });
    };

    // Waiting for default values to load
    if (init.status === 'loading') {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" width={1} height={1}>
                <CircularProgress />
            </Box>
        );
    }

    // Error loading data for product form
    if (init.status === 'error') {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" width={1} height={1}>
                <Typography align="center">There was a problem loading your form</Typography>
            </Box>
        );
    }

    return (
        <ProductForm
            hostId={hostId}
            eventId={eventId}
            autoFocus
            defaultValues={init.defaultValues}
            labelColors={init.labelColors}
            productGroups={init.productGroups}
            enablePOS={enablePOS}
            enableQuantity={enableQuantity}
            enableMultiScan={enableMultiScan}
            enableSeatPricing={enableSeatPricing}
            enableGroups={productType !== 3 && (init.productGroups?.length ?? 0) > 0}
            enableSponsor={init.enableSponsor}
            attendeeRegistration={init.attendeeRegistration}
            onSubmit={handleSubmit}
            onCancel={onCancel}
            loading={loading}
        />
    );
}
